import { useCallback, useEffect, useState } from 'react';
import styles from './GameListPage.module.css';
import { getPage } from '../../helpers/api';
import { firstChineseChar } from '../../helpers/firstChineseChar';

type Game = {
  id: number;
  gameId: string;
  gameName: string;
  price: number;
  hot: number;
};

const PAGE_SIZE = 6;
const GAME_COLORS = ['blue', 'purple', 'orange', 'red', 'cyan'];

const formatYuan = (n: number) =>
  `¥${Number(n || 0).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const GamepadIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M7 8h10a5 5 0 0 1 4.6 7.1l-1.1 2.4a2 2 0 0 1-3.5.2l-1.7-2.4H8.7L7 17.7a2 2 0 0 1-3.5-.2l-1.1-2.4A5 5 0 0 1 7 8z" />
    <path d="M8 12v4" />
    <path d="M6 14h4" />
    <circle cx="16" cy="13" r="1" />
    <circle cx="18.5" cy="15" r="1" />
  </svg>
);

export const GameListPage = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingDir, setLoadingDir] = useState<'prev' | 'next' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [spinning, setSpinning] = useState(false);

  const fetchGames = useCallback(
    async (pageNum: number, dir: 'prev' | 'next' | null = null) => {
      setLoading(true);
      setLoadingDir(dir);
      setError(null);
      try {
        const result = await getPage<Game>(
          `/admin/tel/game/hot?page=${pageNum}&limit=${PAGE_SIZE}`
        );
        setGames(result.records);
        setPage(result.current);
        setPages(result.pages);
        setTotal(result.total);
      } catch (e) {
        setError(e instanceof Error ? e.message : '加载失败');
      } finally {
        setLoading(false);
        setLoadingDir(null);
      }
    },
    []
  );

  useEffect(() => {
    fetchGames(1);
  }, [fetchGames, refreshKey]);

  const handleRefresh = () => {
    setSpinning(true);
    setRefreshKey((key) => key + 1);
    setTimeout(() => setSpinning(false), 600);
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="section-title">
          <div className="icon-box">
            <GamepadIcon />
          </div>
          热门游戏
        </div>
        <div className="card-header-right">
          <button type="button" className="card-refresh" onClick={handleRefresh}>
            <svg
              className={spinning ? 'card-refresh-spin' : ''}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 11a8 8 0 0 0-14-5L3 8" />
              <path d="M3 4v4h4" />
              <path d="M4 13a8 8 0 0 0 14 5l3-2" />
              <path d="M21 20v-4h-4" />
            </svg>
          </button>
          <div className="section-tag">{total} 个</div>
        </div>
      </div>
      <div className={styles.head}>
        <div className={styles.headInner}>
          <span className={styles.headSpacer} />
          <span className={styles.headName}>游戏</span>
          <span className={styles.headHot}>热度</span>
          <span className={styles.headPrice}>价格</span>
        </div>
      </div>
      <div className={`${styles.list} ${loading ? styles.loading : ''}`}>
        {games.map((game, index) => (
          <div key={game.id} className={styles.item}>
            <span
              className={`game-icon ${GAME_COLORS[index % GAME_COLORS.length]}`}
            >
              <span className={styles.letter}>
                {firstChineseChar(game.gameName || game.gameId || '')}
              </span>
            </span>
            <div className={styles.info}>
              <span className={styles.name}>{game.gameName || game.gameId}</span>
              <span className={styles.sub}>{game.gameId}</span>
            </div>
            <span className={styles.hot}>
              <svg
                className={styles.hotIcon}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
              </svg>
              {Number(game.hot || 0).toLocaleString()}
            </span>
            <span className={styles.price}>{formatYuan(game.price)}</span>
          </div>
        ))}
        {games.length === 0 && !loading && !error && (
          <div className={styles.empty}>暂无热门游戏</div>
        )}
      </div>
      {error && <div className="error">{error}</div>}
      {pages > 1 && (
        <div className="pagination">
          <button
            type="button"
            className="page-btn prev"
            disabled={loading || page <= 1}
            onClick={() => fetchGames(page - 1, 'prev')}
          >
            {loading && loadingDir === 'prev' ? <span className="spinner" /> : <span>&lt;</span>}
            上一页
          </button>
          <span className="page-number">{page} / {pages}</span>
          <button
            type="button"
            className="page-btn next"
            disabled={loading || page >= pages}
            onClick={() => fetchGames(page + 1, 'next')}
          >
            下一页
            {loading && loadingDir === 'next' ? <span className="spinner" /> : <span>&gt;</span>}
          </button>
        </div>
      )}
    </div>
  );
};
